import { SuspenseSelect } from '@/components/Suspense';
import { PaymentStatusSelect as PaymentStatusSelectBase } from '@/components/PaymentStatusSelect';
import { useGetInvoiceReportsPaymentStatuses } from '@/context/services/invoice_reports/InvoiceReportSearch.service';
import { InvoiceReportsSearchFormData } from '@/features/invoice_reports/components/Search/hooks/useSearchForm';
import { useTranslation } from '@/i18n';
import { SearchInvoiceReportsQueryPaymentStatus } from 'ap-openapi';
import { FC, memo, useMemo } from 'react';
import { Control, FieldPath } from 'react-hook-form';

type PaymentStatusSelectProps = {
  control: Control<InvoiceReportsSearchFormData>;
  name: FieldPath<InvoiceReportsSearchFormData>;
  placeholder?: string;
};

const _PaymentStatusSelect: FC<PaymentStatusSelectProps> = memo(
  ({ control, name, placeholder }) => {
    const { t } = useTranslation();
    const { data } = useGetInvoiceReportsPaymentStatuses();

    const options = useMemo(
      () =>
        data?.data?.map((status) => {
          const value = status.value ?? '';
          const tooltip =
            value === SearchInvoiceReportsQueryPaymentStatus.not_yet_paid
              ? t('invoice_reports.search.payment_status.not_yet_paid_tooltip')
              : value === SearchInvoiceReportsQueryPaymentStatus.paid
              ? t('invoice_reports.search.payment_status.paid_tooltip')
              : undefined;
          return { label: status.display_name ?? '', value, tooltip };
        }) ?? [],
      [data?.data, t]
    );

    return (
      <PaymentStatusSelectBase<InvoiceReportsSearchFormData>
        control={control}
        name={name}
        options={options}
        placeholder={placeholder}
      />
    );
  }
);

_PaymentStatusSelect.displayName = '_PaymentStatusSelect';

export const PaymentStatusSelect: FC<PaymentStatusSelectProps> = memo(
  (props) => (
    <SuspenseSelect>
      <_PaymentStatusSelect {...props} />
    </SuspenseSelect>
  )
);

PaymentStatusSelect.displayName = 'PaymentStatusSelect';
